/**
 * Request Context using Next.js cache API
 *
 * This provides request-scoped storage to prevent data leakage between
 * concurrent requests in server-side rendering.
 *
 * SECURITY: This replaces module-level caches that could leak data
 * between users in concurrent requests.
 *
 * EDGE COMPATIBLE: Uses Next.js cache() API which works in both Node.js
 * and Edge runtimes, unlike AsyncLocalStorage which requires Node.js.
 */

import { cache } from 'react';

export interface RequestContext {
  // Request identification
  requestId?: string;
  userId?: string;
  tenantId?: string;

  // Session data cache (request-scoped)
  sessionData?: any;
  sessionCacheTimestamp?: number;

  // Request metadata
  startTime?: number;
  headers?: Record<string, string>;
}

/**
 * Edge-compatible request context storage using React cache()
 * React's cache() provides request-scoped memoization that works in Edge runtime
 */
const getRequestContextStore = cache((): RequestContext => {
  // Create a new context for each request (cache() ensures one per request)
  return {
    requestId: `req-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    startTime: Date.now(),
  };
});

/**
 * Get the current request context
 * Returns undefined if called outside of a request context
 */
export function getRequestContext(): RequestContext | undefined {
  try {
    return getRequestContextStore();
  } catch {
    return undefined;
  }
}

/**
 * Set the request context for the current async operation
 * Note: With React cache(), context is automatically request-scoped
 */
export function setRequestContext<T>(context: RequestContext, callback: () => T): T {
  // Merge context into the current request's context
  const currentContext = getRequestContextStore();
  Object.assign(currentContext, context);
  return callback();
}

/**
 * Get or create a request context with automatic initialization
 */
export function getOrCreateRequestContext(): RequestContext {
  try {
    return getRequestContextStore();
  } catch {
    // Fallback for non-request contexts (shouldn't happen in normal operation)
    console.warn('Request context not found, creating fallback. This may indicate improper request scoping.');
    return {
      requestId: `fallback-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      startTime: Date.now(),
    };
  }
}

/**
 * Cache session data in the request context (request-scoped)
 * This replaces the module-level sessionCache
 */
export function cacheSessionDataInContext(data: any): void {
  const context = getOrCreateRequestContext();
  context.sessionData = data;
  context.sessionCacheTimestamp = Date.now();
}

/**
 * Get cached session data from request context
 * Returns null if cache is expired or doesn't exist
 */
export function getCachedSessionDataFromContext(ttl: number = 1000): any | null {
  const context = getRequestContext();

  if (!context?.sessionData || !context?.sessionCacheTimestamp) {
    return null;
  }

  const now = Date.now();
  if (now - context.sessionCacheTimestamp > ttl) {
    // Cache expired
    return null;
  }

  return context.sessionData;
}

/**
 * Clear cached session data from request context
 */
export function clearSessionCacheInContext(): void {
  const context = getRequestContext();
  if (context) {
    context.sessionData = null;
    context.sessionCacheTimestamp = undefined;
  }
}

/**
 * Create a cache key scoped to the current request's user/tenant
 * This ensures caches don't leak between users
 */
export function createScopedCacheKey(baseKey: string): string {
  const context = getRequestContext();

  if (!context) {
    // Fallback to just the base key if no context
    return baseKey;
  }

  const parts = [baseKey];

  if (context.userId) {
    parts.push(`user:${context.userId}`);
  }

  if (context.tenantId) {
    parts.push(`tenant:${context.tenantId}`);
  }

  if (context.requestId) {
    parts.push(`req:${context.requestId}`);
  }

  return parts.join('|');
}

/**
 * Middleware helper to initialize request context
 * Call this at the start of each request
 */
export function initializeRequestContext(options: {
  userId?: string;
  tenantId?: string;
  headers?: Record<string, string>;
}): RequestContext {
  return {
    requestId: `req-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    userId: options.userId,
    tenantId: options.tenantId,
    headers: options.headers,
    startTime: Date.now(),
  };
}
